import type { ReactElement, ReactNode } from 'react';
import { Children, Fragment, isValidElement, useMemo } from 'react';
import {
  NativeSelect,
  Switch as MantineSwitch,
  Textarea as MantineTextarea,
  TextInput,
  type NativeSelectProps,
  type SwitchProps,
  type TextareaProps,
  type TextInputProps,
} from '@mantine/core';

export function Input(props: TextInputProps) {
  return <TextInput {...props} />;
}

export function Textarea(props: TextareaProps) {
  return <MantineTextarea {...props} />;
}

/** Labeled boolean control — preferred over legacy ToggleField checkbox. */
export function Switch({
  id,
  label,
  description,
  checked,
  disabled,
  onChange,
  ...props
}: {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
} & Omit<SwitchProps, 'id' | 'label' | 'description' | 'checked' | 'disabled' | 'onChange'>) {
  return (
    <MantineSwitch
      id={id}
      data-testid={id}
      label={label}
      description={description}
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.currentTarget.checked)}
      {...props}
    />
  );
}

function isOptionElement(child: ReactElement): boolean {
  return typeof child.type === 'string' && child.type.toLowerCase() === 'option';
}

function optionsToSelectData(children: ReactNode): Array<{ value: string; label: string; disabled?: boolean }> {
  return Children.toArray(children)
    .filter(isValidElement)
    .flatMap((child) => {
      if (isOptionElement(child)) {
        const props = child.props as { value?: string | number; disabled?: boolean; children?: ReactNode };
        const label = typeof props.children === 'string' || typeof props.children === 'number'
          ? String(props.children)
          : Children.toArray(props.children).join('');

        return [{
          value: String(props.value ?? ''),
          label,
          disabled: props.disabled,
        }];
      }

      if (child.type === Fragment) {
        return optionsToSelectData((child.props as { children?: ReactNode }).children);
      }

      return [];
    });
}

/** Native select with optional `<option>` children compatibility. */
export function Select({ children, data, ...props }: NativeSelectProps) {
  const resolvedData = useMemo(() => {
    if (data !== undefined) {
      return data;
    }

    if (!children) {
      return [];
    }

    return optionsToSelectData(children);
  }, [children, data]);

  return <NativeSelect data={resolvedData} {...props} />;
}
